import React from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutGrid,
    ShoppingBag,
    Package,
    Headphones,
    Users,
    Settings,
    ChevronLeft,
    ChevronRight,
    MessageSquare,
} from 'lucide-react';
import { visibleNavItemsForRole } from '../../lib/routeConfig';

const ICON_MAP = {
    LayoutGrid,
    ShoppingBag,
    Package,
    Headphones,
    Users,
    Settings,
    MessageSquare,
};

// Sidebar — fixed, left, full height. 220px expanded / 64px collapsed.
export default function Sidebar({ user, collapsed, onToggle }) {
    const items = visibleNavItemsForRole(user?.role);
    const widthClass = collapsed ? 'w-16' : 'w-[220px]';

    return (
        <aside
            data-testid="sidebar"
            data-collapsed={collapsed ? 'true' : 'false'}
            className={`${widthClass} shrink-0 h-screen sticky top-0 bg-white border-r border-[#E5E7EB] flex flex-col`}
            style={{ transition: 'width 200ms ease' }}
        >
            {/* Wordmark */}
            <div className="h-14 flex items-center px-4 border-b border-[#E5E7EB]">
                {collapsed ? (
                    <img
                        src="/logos/nivo-mark.png"
                        alt="Nivo"
                        data-testid="sidebar-wordmark-collapsed"
                        className="h-8 w-auto object-contain mx-auto"
                        draggable={false}
                    />
                ) : (
                    <img
                        src="/logos/nivo-lockup.png"
                        alt="Nivo"
                        data-testid="sidebar-wordmark"
                        className="h-8 w-auto object-contain"
                        draggable={false}
                    />
                )}
            </div>

            {/* Section label */}
            {!collapsed && (
                <div
                    data-testid="sidebar-section-label"
                    className="px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]"
                >
                    MAIN
                </div>
            )}
            {collapsed && <div className="h-4" />}

            {/* Nav items */}
            <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
                {items.map((item) => {
                    const Icon = ICON_MAP[item.icon];
                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-${item.path.replace(/^\//, '')}`}
                            className={({ isActive }) =>
                                [
                                    'flex items-center h-10 rounded-lg text-sm gap-3',
                                    collapsed ? 'justify-center px-0' : 'px-3',
                                    isActive
                                        ? 'bg-[#4B3FD8]/10 text-[#4B3FD8] font-medium'
                                        : 'text-[#6B7280] hover:bg-[#F5F6FA]',
                                ].join(' ')
                            }
                            style={{ transitionProperty: 'background-color, color' }}
                            title={collapsed ? item.title : undefined}
                        >
                            {Icon && <Icon size={18} strokeWidth={1.75} className="shrink-0" />}
                            {!collapsed && <span className="truncate">{item.title}</span>}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Toggle */}
            <div className="p-2 border-t border-[#E5E7EB]">
                <button
                    type="button"
                    data-testid="sidebar-toggle"
                    onClick={onToggle}
                    className={`w-full h-10 flex items-center gap-2 rounded-lg text-[#6B7280] hover:bg-[#F5F6FA] ${
                        collapsed ? 'justify-center px-0' : 'px-3'
                    }`}
                    style={{ transitionProperty: 'background-color' }}
                    aria-label={collapsed ? 'Ouvrir la sidebar' : 'Réduire la sidebar'}
                >
                    {collapsed ? (
                        <ChevronRight size={18} strokeWidth={1.75} />
                    ) : (
                        <>
                            <ChevronLeft size={18} strokeWidth={1.75} />
                            <span className="text-sm">Réduire</span>
                        </>
                    )}
                </button>
            </div>
        </aside>
    );
}
